'use client'

import { forwardRef, type RefObject } from 'react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'

// Cloudflare Turnstile — required by Supabase Auth once "Enable Captcha" is on
// (applies to signUp, signInWithPassword and resetPasswordForEmail together).
// Without the env var the widget is not rendered and no token is sent, which
// is fine as long as captcha stays disabled in Supabase.
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

export type CaptchaRef = RefObject<TurnstileInstance | null>

export const Captcha = forwardRef<TurnstileInstance>(function Captcha(_props, ref) {
  if (!SITE_KEY) return null
  return (
    <Turnstile
      ref={ref}
      siteKey={SITE_KEY}
      // Invisible unless Cloudflare really needs the visitor to click.
      options={{ appearance: 'interaction-only', size: 'flexible', refreshExpired: 'auto' }}
    />
  )
})

/**
 * Waits for the widget to produce a token — it may still be loading or solving
 * (the library's getResponsePromise rejects right away in that case).
 * Throws CaptchaError when no token arrives in time.
 */
export async function getCaptchaToken(ref: CaptchaRef, timeoutMs = 15000): Promise<string | undefined> {
  if (!SITE_KEY) return undefined
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const token = ref.current?.getResponse()
    if (token) return token
    await new Promise(resolve => setTimeout(resolve, 200))
  }
  throw new CaptchaError()
}

export class CaptchaError extends Error {
  constructor() {
    super('Captcha token not available')
    this.name = 'CaptchaError'
  }
}

/** True for a missing token or Supabase rejecting it (error.code 'captcha_failed'). */
export function isCaptchaError(error: unknown): boolean {
  return error instanceof CaptchaError || (error as any)?.code === 'captcha_failed'
}

/** Tokens are single-use — call after every request that consumed one. */
export function resetCaptcha(ref: CaptchaRef) {
  ref.current?.reset()
}
